import { RpcError, RpcRedirect } from "@buildpy/api/client";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, isNotFound, type AnyRoute, type ErrorComponentProps } from "@tanstack/react-router";
import type { ReactElement } from "react";

import { withRouteRetry } from "@/app/route-retry";
import { loadView, publicViewQuery } from "@/api/views";
import { customerHead, NOINDEX_ROBOTS, withRobots } from "@/lib/head";
import IntakeError from "@/pages/s/intake/[slug]/error";
import IntakeNotFound from "@/pages/s/intake/[slug]/not-found";
import IntakePage from "@/pages/s/intake/[slug]/page";
import PublicPageError from "@/pages/s/p/[code]/error";
import PublicPageNotFound from "@/pages/s/p/[code]/not-found";
import PublicPagePage from "@/pages/s/p/[code]/page";
import PortfolioDetailPage from "@/pages/s/portfolio/[slug]/[id]/page";
import PortfolioEmbedPage from "@/pages/s/portfolio/[slug]/embed/page";
import PortfolioError from "@/pages/s/portfolio/[slug]/error";
import PortfolioNotFound from "@/pages/s/portfolio/[slug]/not-found";
import PortfolioListPage from "@/pages/s/portfolio/[slug]/page";
import ReferralError from "@/pages/s/r/[code]/error";
import ReferralNotFound from "@/pages/s/r/[code]/not-found";
import ReferralPage from "@/pages/s/r/[code]/page";

import { sRoute } from "./w0b";

type FailureComponent = (props: { error: Error & { digest?: string }; reset: () => void }) => ReactElement;

// 화면마다 legacy 의 not-found·error 를 그대로 — 이동 신호는 화면이 아니고, 404 는 그 화면의 안내로.
const publicBoundaries = (Missing: () => ReactElement, Failure: FailureComponent) => ({
  notFoundComponent: Missing,
  errorComponent: withRouteRetry(({ error, reset }: ErrorComponentProps) => {
    if (error instanceof RpcRedirect) return null;
    if (isNotFound(error) || (error instanceof RpcError && error.status === 404)) return <Missing />;
    return <Failure error={error as Error & { digest?: string }} reset={reset} />;
  }),
});

// 제목은 읽어 둔 값 — 못 읽었으면(404·오류) 기본 제목에 noindex(Next 가 404 에 붙이던 것과 같다).
const publicHead = (title: string | undefined, fallback: string, noindex = false) =>
  withRobots(customerHead(title ?? fallback), noindex || title === undefined ? NOINDEX_ROBOTS : null);

// 쿼리는 사람이 고칠 수 있다 — 글자만 받고 나머지는 버린다(값 판정은 core 파서가 한다).
const textOf = (value: unknown): string | undefined =>
  typeof value === "string" ? value : typeof value === "number" ? String(value) : undefined;

interface ViaSearch {
  via?: string;
}

const viaSearch = (search: Record<string, unknown>): ViaSearch => ({ via: textOf(search.via) });

interface IntakeSearch {
  site?: string;
  from?: string;
  page?: string;
  case?: string;
  ref?: string;
}

// 화면 route 의 validateSearch — Worker 가 같은 값으로 읽어 실어야 hydrate 캐시 키가 맞는다.
export const intakeSearchOf = (search: Record<string, unknown>): IntakeSearch => ({
  site: textOf(search.site),
  from: textOf(search.from),
  page: textOf(search.page),
  case: textOf(search.case),
  ref: textOf(search.ref),
});

// Worker 가 SSR 캐시로 실어 주는 읽기 — hydrate 뒤 mount 가 suspense 기본 1초를 넘겨도 다시 읽지 않게(한 번 열 때 방문자 한도를 한 번만 센다).
const SEEDED_STALE_MS = 60_000;
const seeded = <T extends object>(options: T): T & { staleTime: number } => ({ ...options, staleTime: SEEDED_STALE_MS });

export const intakeQuery = (slug: string, search: IntakeSearch) => seeded(publicViewQuery("intake", [slug], search));

const IntakeRouteComponent = () => {
  const { slug } = intakeRoute.useParams();
  const { data } = useSuspenseQuery(intakeQuery(slug, intakeRoute.useSearch()));
  return <IntakePage data={data} />;
};

export const intakeRoute = createRoute({
  getParentRoute: () => sRoute,
  path: "/intake/$slug",
  validateSearch: intakeSearchOf,
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => loadView(context.queryClient, intakeQuery(params.slug, deps)),
  head: ({ match, params }) =>
    publicHead(match.context.queryClient.getQueryData(intakeQuery(params.slug, match.search).queryKey)?.title, "상담 접수", true),
  component: IntakeRouteComponent,
  ...publicBoundaries(IntakeNotFound, IntakeError),
});

export const publicPageQuery = (code: string) => seeded(publicViewQuery("public-page", [code]));

const PublicPageRouteComponent = () => {
  const { code } = publicPageRoute.useParams();
  const { data } = useSuspenseQuery(publicPageQuery(code));
  return <PublicPagePage data={data} />;
};

export const publicPageRoute = createRoute({
  getParentRoute: () => sRoute,
  path: "/p/$code",
  loader: ({ context, params }) => loadView(context.queryClient, publicPageQuery(params.code)),
  head: ({ match, params }) => publicHead(match.context.queryClient.getQueryData(publicPageQuery(params.code).queryKey)?.title, "시공사례"),
  component: PublicPageRouteComponent,
  ...publicBoundaries(PublicPageNotFound, PublicPageError),
});

const referralQuery = (code: string) => publicViewQuery("referral", [code]);

const ReferralRouteComponent = () => {
  const { code } = referralRoute.useParams();
  const { data } = useSuspenseQuery(referralQuery(code));
  return <ReferralPage data={data} />;
};

// 거쳐 가는 주소라 색인하지 않는다 — 이동은 view 의 redirect 를 loader 가 라우터 이동으로 바꾼다(배포는 Worker 가 302 로 먼저 보낸다).
export const referralRoute = createRoute({
  getParentRoute: () => sRoute,
  path: "/r/$code",
  loader: ({ context, params }) => loadView(context.queryClient, referralQuery(params.code)),
  head: () => publicHead("상담 접수", "상담 접수", true),
  component: ReferralRouteComponent,
  ...publicBoundaries(ReferralNotFound, ReferralError),
});

export const portfoliosQuery = (slug: string, search: ViaSearch) => seeded(publicViewQuery("public-portfolios", [slug], search));

const PortfolioListRouteComponent = () => {
  const { slug } = portfolioListRoute.useParams();
  const { data } = useSuspenseQuery(portfoliosQuery(slug, portfolioListRoute.useSearch()));
  return <PortfolioListPage data={data} />;
};

export const portfolioListRoute = createRoute({
  getParentRoute: () => sRoute,
  path: "/portfolio/$slug",
  validateSearch: viaSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => loadView(context.queryClient, portfoliosQuery(params.slug, deps)),
  head: ({ match, params }) =>
    publicHead(match.context.queryClient.getQueryData(portfoliosQuery(params.slug, match.search).queryKey)?.title, "시공사례"),
  component: PortfolioListRouteComponent,
  ...publicBoundaries(PortfolioNotFound, PortfolioError),
});

export const portfolioQuery = (slug: string, id: string, search: ViaSearch) => seeded(publicViewQuery("public-portfolio", [slug, id], search));

const PortfolioDetailRouteComponent = () => {
  const { slug, id } = portfolioDetailRoute.useParams();
  const { data } = useSuspenseQuery(portfolioQuery(slug, id, portfolioDetailRoute.useSearch()));
  return <PortfolioDetailPage data={data} />;
};

export const portfolioDetailRoute = createRoute({
  getParentRoute: () => sRoute,
  path: "/portfolio/$slug/$id",
  validateSearch: viaSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => loadView(context.queryClient, portfolioQuery(params.slug, params.id, deps)),
  head: ({ match, params }) =>
    publicHead(match.context.queryClient.getQueryData(portfolioQuery(params.slug, params.id, match.search).queryKey)?.title, "시공사례"),
  component: PortfolioDetailRouteComponent,
  ...publicBoundaries(PortfolioNotFound, PortfolioError),
});

const embedQuery = (slug: string) => publicViewQuery("public-portfolio-embed", [slug]);

const PortfolioEmbedRouteComponent = () => {
  const { slug } = portfolioEmbedRoute.useParams();
  const { data } = useSuspenseQuery(embedQuery(slug));
  return <PortfolioEmbedPage data={data} />;
};

// 남의 홈페이지 iframe 안에서만 보이는 조각이라 검색에는 원래 목록만 나가게 한다.
export const portfolioEmbedRoute = createRoute({
  getParentRoute: () => sRoute,
  path: "/portfolio/$slug/embed",
  loader: ({ context, params }) => loadView(context.queryClient, embedQuery(params.slug)),
  head: () => publicHead("시공사례", "시공사례", true),
  component: PortfolioEmbedRouteComponent,
  ...publicBoundaries(PortfolioNotFound, PortfolioError),
});

// g4b 배치의 라우트 — 로그인 없는 공개 화면 6개.
export const routes: AnyRoute[] = [intakeRoute, publicPageRoute, referralRoute, portfolioListRoute, portfolioDetailRoute, portfolioEmbedRoute];
